"use client";

import * as RadixDialog from "@radix-ui/react-dialog";
import { forwardRef, ReactNode } from "react";
import { withClassName } from "../../utils";

// Desktop width only — every size is full-width (bottom sheet) on mobile.
// Reach for "lg" when the dialog holds long-form copy that would otherwise
// scroll out of sight in the default column, and "xl" when that copy is laid
// out in columns and needs the room to sit side by side.
const containerWidth = {
  md: "md:w-120",
  lg: "md:w-140 lg:w-160",
  xl: "md:w-160 lg:w-200 xl:w-240",
};

interface ContainerProps {
  children: ReactNode;
  size?: keyof typeof containerWidth;
}

const Container = ({ children, size = "md" }: ContainerProps) => {
  return (
    <RadixDialog.Portal>
      <RadixDialog.Overlay className="fixed inset-0 bg-black/75 z-50 data-[state=open]:animate-fadeIn data-[state=closed]:animate-fadeOut" />
      <RadixDialog.Content
        className={`flex flex-col fixed w-full left-0 right-0 bottom-0 ${containerWidth[size]} max-w-full max-h-[90vh] md:max-h-[80vh] md:translate-[-50%] md:top-1/2 md:left-1/2 md:bottom-auto data-[state=open]:animate-slideUp data-[state=closed]:animate-slideDown md:data-[state=open]:animate-fadeInScaleUp md:data-[state=closed]:animate-fadeOutScaleDown bg-white shadow-lg z-50 outline-none rounded-t-xl md:rounded-xl`}
      >
        {children}
      </RadixDialog.Content>
    </RadixDialog.Portal>
  );
};

interface HeaderProps {
  children: ReactNode;
}

const Header = ({ children }: HeaderProps) => {
  return (
    <header className="flex flex-shrink-0 items-center justify-between border-b border-light px-4 py-4">
      {children}
    </header>
  );
};

interface ContentProps {
  children: ReactNode;
}

const Content = forwardRef<HTMLDivElement, ContentProps>(({ children }, ref) => {
  return (
    <div ref={ref} className="flex flex-1 flex-col p-4 overflow-y-auto">{children}</div>
  );
});
Content.displayName = "Dialog.Content";

interface FooterProps {
  children: ReactNode;
}

const Footer = ({ children }: FooterProps) => {
  return (
    <footer className="flex flex-shrink-0 border-t border-light px-4 pt-4 pb-8 md:pb-4">
      {children}
    </footer>
  );
};

const Title = withClassName(
  RadixDialog.Title,
  "text-base font-medium leading-tight",
);

const Description = withClassName(
  RadixDialog.Description,
  "text-sm text-gray-1000 leading-tight",
);

const Root = RadixDialog.Root;
const Close = RadixDialog.Close;
const Trigger = RadixDialog.Trigger;

export const Dialog = {
  Container,
  Header,
  Content,
  Footer,
  Title,
  Description,
  Root,
  Close,
  Trigger,
};
